// CMS rich-text parsing: html-react-parser options + the helpers that use them.
import parse, { attributesToProps, domToReact } from 'html-react-parser'

import { cmsImageSrc, cmsImageSrcSet } from '@/utils/cmsImageSrc'
import { canonicalHref, mailtoHref, resolveRenamedPath } from '@/utils/href'

/**
 * Normalize a rich-text <img>: local optimized src when one exists, a responsive
 * srcSet when the optimizer emitted more than one width, and an explicit empty
 * alt when the editor left it off. Returns undefined when none of those apply,
 * so html-react-parser keeps the original node.
 *
 * `sizes` is the conservative 100vw default: rich-text images are authored in
 * Strapi and this code cannot know their rendered width, and 100vw is also what
 * the browser assumes when `sizes` is absent. It still helps — a 390px phone
 * picks the 480w candidate where before it always downloaded the single 960w
 * encode.
 */
function replaceImg(attribs) {
  const { alt, src, ...rest } = attribs ?? {}
  const optimized = cmsImageSrc(src)
  const srcSet = cmsImageSrcSet(src)
  if (alt && optimized === src && !srcSet) return undefined
  return (
    <img
      {...attributesToProps(rest)}
      src={optimized}
      srcSet={srcSet}
      sizes={srcSet ? '100vw' : undefined}
      alt={alt ?? ''}
    />
  )
}

/**
 * Drop-in replacement for `parse()` for CMS HTML blocks.
 * - Adds alt="" to any <img> missing an alt so axe-core does not flag it, and
 *   points its src at the build-optimized local WebP when the image optimizer
 *   produced one (editors paste full-size CMS art straight into rich text).
 * - Normalizes internal <a href> to the canonical trailing-slash form so links
 *   inside CMS rich text stay client-side (raw anchors bypass the <Link> wrapper;
 *   under `trailingSlash: true` a missing slash forces a full page reload).
 * - Maps hrefs pointing at renamed routes to their current path, so rich text
 *   still carrying a pre-rename slug does not ship a hard 404 (see
 *   {@link resolveRenamedPath}).
 * - Prefixes a bare email address href with `mailto:` (see {@link mailtoHref}) —
 *   editors paste these in constantly and the browser otherwise resolves them as
 *   a relative path.
 */
const CMS_PARSE_OPTIONS = {
  replace(node) {
    if (node.type === 'tag' && node.name === 'img') {
      const img = replaceImg(node.attribs)
      if (img) return img
    }
    if (node.type === 'tag' && node.name === 'a' && node.attribs?.href) {
      const canonical = mailtoHref(canonicalHref(resolveRenamedPath(node.attribs.href)))
      if (canonical !== node.attribs.href) {
        return (
          <a {...attributesToProps({ ...node.attribs, href: canonical })}>
            {domToReact(node.children, CMS_PARSE_OPTIONS)}
          </a>
        )
      }
    }
  }
}

// Same as CMS_PARSE_OPTIONS but also downgrades <a> to <span>. Use when the parsed
// content is rendered inside another anchor (e.g. a card linking to a detail page),
// since nested <a> tags are invalid HTML and trigger React hydration errors.
const CMS_PARSE_OPTIONS_NO_LINKS = {
  replace(node) {
    if (node.type === 'tag' && node.name === 'img') {
      const img = replaceImg(node.attribs)
      if (img) return img
    }
    if (node.type === 'tag' && node.name === 'a') {
      const { href: _href, target: _target, rel: _rel, ...rest } = node.attribs ?? {}
      return (
        <span {...attributesToProps(rest)}>
          {domToReact(node.children, CMS_PARSE_OPTIONS_NO_LINKS)}
        </span>
      )
    }
  }
}

export function parseCmsHtml(html, { stripLinks = false } = {}) {
  if (!html) return null
  return parse(html, stripLinks ? CMS_PARSE_OPTIONS_NO_LINKS : CMS_PARSE_OPTIONS)
}

export function preserveHyphenatedWords(text) {
  if (!text) return null

  // Replace tightly connected hyphenated sequences (including HTML tags)
  const modifiedText = text.replaceAll(
    /([^\s>]+(?:<[^>]+>)*?)-([^\s<]+(?:<[^>]+>)*?)/g,
    (_match, before, after) => {
      return `<span className="no-wrap">${before}-${after}</span>`
    }
  )

  return parse(modifiedText)
}
